import { supabase } from './supabase'

export type InboxEntityLinkKey =
  | 'customer_id'
  | 'lead_id'
  | 'property_id'
  | 'contract_id'
  | 'job_id'
  | 'employee_id'
  | 'proposal_id'

export interface InboxEntityLinkField {
  key: InboxEntityLinkKey
  label: string
  table: string
  labelColumn: string
  route: (id: string) => string
}

/** CRM record types staff can link to inbox threads / call logs (not staff assignee). */
export const INBOX_ENTITY_LINK_FIELDS: InboxEntityLinkField[] = [
  {
    key: 'customer_id',
    label: 'Customer',
    table: 'customers',
    labelColumn: 'company_name',
    route: (id) => `/customers/view/${id}`,
  },
  {
    key: 'lead_id',
    label: 'Lead',
    table: 'leads',
    labelColumn: 'company_name',
    route: (id) => `/leads/view/${id}`,
  },
  {
    key: 'property_id',
    label: 'Property',
    table: 'properties',
    labelColumn: 'address',
    route: (id) => `/properties/view/${id}`,
  },
  {
    key: 'contract_id',
    label: 'Contract',
    table: 'contracts',
    labelColumn: 'contract_number',
    route: (id) => `/contracts/details/${id}`,
  },
  {
    key: 'job_id',
    label: 'Job',
    table: 'jobs',
    labelColumn: 'job_number',
    route: (id) => `/jobs/details/${id}`,
  },
  {
    key: 'employee_id',
    label: 'Employee',
    table: 'employees',
    labelColumn: 'first_name',
    route: () => `/settings/team`,
  },
  {
    key: 'proposal_id',
    label: 'Proposal',
    table: 'proposals',
    labelColumn: 'proposal_number',
    route: (id) => `/proposals/view/${id}`,
  },
]

export async function searchInboxEntityLinks(
  entityKey: InboxEntityLinkKey,
  term: string,
  limit = 8
): Promise<Array<{ id: string; label: string }>> {
  const trimmed = term.trim()
  if (!trimmed) return []

  const field = INBOX_ENTITY_LINK_FIELDS.find((f) => f.key === entityKey)
  if (!field) return []

  if (entityKey === 'customer_id') {
    const pattern = `%${trimmed}%`
    const { data } = await supabase
      .from('customers')
      .select('id, company_name, email, phone')
      .or(`company_name.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern}`)
      .order('company_name')
      .limit(limit)
    return (data || []).map((row: any) => ({
      id: row.id,
      label: row.company_name || row.email || row.phone || 'Unnamed customer',
    }))
  }

  if (entityKey === 'employee_id') {
    const { data } = await supabase
      .from('employees')
      .select('id, first_name, last_name')
      .or(`first_name.ilike.%${trimmed}%,last_name.ilike.%${trimmed}%`)
      .limit(limit)
    return (data || []).map((row: any) => ({
      id: row.id,
      label: `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.id,
    }))
  }

  const searchCol = field.labelColumn
  const { data } = await supabase
    .from(field.table)
    .select(`id, ${searchCol}`)
    .ilike(searchCol, `%${trimmed}%`)
    .limit(limit)

  return (data || []).map((row: any) => ({
    id: row.id,
    label: row[searchCol] || row.id,
  }))
}

export async function fetchInboxEntityLinkLabel(
  entityKey: InboxEntityLinkKey,
  id: string
): Promise<string> {
  const field = INBOX_ENTITY_LINK_FIELDS.find((f) => f.key === entityKey)
  if (!field) return id

  if (entityKey === 'employee_id') {
    const { data } = await supabase.from('employees').select('first_name, last_name').eq('id', id).single()
    if (data) return `${data.first_name || ''} ${data.last_name || ''}`.trim() || id
    return id
  }

  const { data } = await supabase.from(field.table).select(field.labelColumn).eq('id', id).single()
  if (data) return (data as any)[field.labelColumn] || id
  return id
}

export async function fetchInboxEntityLinkLabels(
  links: Partial<Record<InboxEntityLinkKey, string | null | undefined>>
): Promise<Record<string, string>> {
  const labels: Record<string, string> = {}
  await Promise.all(
    INBOX_ENTITY_LINK_FIELDS.map(async (field) => {
      const id = links[field.key]
      if (!id) return
      labels[field.key] = await fetchInboxEntityLinkLabel(field.key, id)
    })
  )
  return labels
}
